/**
 * Tenant Branding Store (Zustand)
 * Fetches and stores tenant branding based on subdomain or ?tenant= query param.
 * Used on the login page and pre-auth screens to show ISO-specific branding.
 */
import { create } from 'zustand';

const API = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULT_BRANDING = {
  company_name: 'ISOCNTRL',
  primary_color: '#3b82f6',
  secondary_color: '#1e293b',
  accent_color: '#10b981',
  sidebar_color: '#ffffff',
  logo_url: null,
  favicon_url: null,
  font: 'Inter',
  subdomain: null,
  tenant_id: null,
};

function detectSubdomain() {
  // 1. Check ?tenant= query param (for testing on preview URLs)
  const params = new URLSearchParams(window.location.search);
  const tenantParam = params.get('tenant');
  if (tenantParam) return tenantParam.toLowerCase().trim();

  // 2. Check actual subdomain: e.g., merchantsolutions.isocntrl.com
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // Skip if it's localhost, IP, or a known non-tenant domain
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null;

  // For *.isocntrl.com → parts[0] is the subdomain
  // For *.preview.emergentagent.com → skip (preview env)
  if (parts.length >= 3) {
    const sub = parts[0].toLowerCase();
    // Skip common non-tenant subdomains
    if (['www', 'app', 'api', 'admin', 'whitelabel-crm-2'].includes(sub)) return null;
    // If it's a known preview domain, skip
    if (hostname.includes('preview.emergentagent.com')) return null;
    return sub;
  }

  return null;
}

const useTenantBrandStore = create((set, get) => ({
  branding: { ...DEFAULT_BRANDING },
  subdomain: null,
  loaded: false,
  loading: false,
  error: null,
  isCustomTenant: false,

  fetchBranding: async (subdomainOverride) => {
    const sub = subdomainOverride || detectSubdomain();
    if (!sub) {
      set({ loaded: true, isCustomTenant: false, subdomain: null });
      return;
    }

    set({ loading: true, subdomain: sub });
    try {
      const resp = await fetch(`${API}/api/tenant/branding/public/${sub}`);
      if (!resp.ok) throw new Error('Tenant not found');
      const data = await resp.json();

      set({
        branding: {
          company_name: data.company_name || DEFAULT_BRANDING.company_name,
          primary_color: data.primary_color || DEFAULT_BRANDING.primary_color,
          secondary_color: data.secondary_color || DEFAULT_BRANDING.secondary_color,
          accent_color: data.accent_color || DEFAULT_BRANDING.accent_color,
          sidebar_color: data.sidebar_color || DEFAULT_BRANDING.sidebar_color,
          logo_url: data.logo_url,
          favicon_url: data.favicon_url,
          font: data.font || 'Inter',
          subdomain: data.subdomain,
          tenant_id: data.tenant_id,
        },
        loaded: true,
        loading: false,
        isCustomTenant: true,
        error: null,
      });

      // Apply dynamic favicon
      if (data.favicon_url) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = data.favicon_url;
      }

      // Apply dynamic page title
      document.title = data.company_name ? `${data.company_name} — CRM` : 'ISOCNTRL — ISO Portal';

    } catch (err) {
      set({ loaded: true, loading: false, error: err.message, isCustomTenant: false });
    }
  },

  // Apply branding from authenticated user's tenant data
  applyUserBranding: (user) => {
    if (!user) return;

    const branding = {
      company_name: user.tenant_name || DEFAULT_BRANDING.company_name,
      primary_color: user.tenant_primary_color || DEFAULT_BRANDING.primary_color,
      secondary_color: user.tenant_secondary_color || DEFAULT_BRANDING.secondary_color,
      accent_color: user.tenant_accent_color || DEFAULT_BRANDING.accent_color,
      sidebar_color: user.tenant_sidebar_color || DEFAULT_BRANDING.sidebar_color,
      logo_url: user.tenant_logo,
      favicon_url: user.tenant_favicon,
      font: user.tenant_font || 'Inter',
      subdomain: user.tenant_subdomain,
      tenant_id: user.tenant_id,
    };

    set({ branding, isCustomTenant: !!user.tenant_name, loaded: true });

    // Dynamic favicon
    if (branding.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = branding.favicon_url;
    }

    // Dynamic title
    document.title = branding.company_name !== 'ISOCNTRL'
      ? `${branding.company_name} — CRM`
      : 'ISOCNTRL — ISO Portal';
  },

  reset: () => {
    set({ branding: { ...DEFAULT_BRANDING }, loaded: false, isCustomTenant: false, subdomain: null, error: null });
    document.title = 'ISOCNTRL — ISO Portal';
  },
}));

export default useTenantBrandStore;
